// packages/agent-kit/src/public/public-agents/useAutoSaveVersion.ts
import { useState, useEffect, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import { agentVersionsService, isApiError } from '@dopomogai/api-client';
import type { AgentVersion, AgentVersionOverrides, UUID } from '@dopomogai/types';
import { useAnalytics } from '../../analytics/useAnalytics';

interface AutoSaveOptions {
  versionId?: UUID;
  overrides?: AgentVersionOverrides;
  debounceMs?: number;
  visitorId?: string;
  onForked?: (newVersion: AgentVersion) => void;
}

/**
 * A hook to automatically save changes to an AgentVersion after a delay.
 */
export const useAutoSaveVersion = ({
  versionId,
  overrides,
  debounceMs = 1500,
  visitorId,
  onForked,
}: AutoSaveOptions) => {
  const analytics = useAnalytics();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const [debouncedOverrides] = useDebounce(overrides, debounceMs);
  const isInitialMount = useRef(true);
  const saveCounterRef = useRef(0);

  // Cache a fork target ID to avoid reforking until parent updates props.
  const forkTargetIdRef = useRef<UUID | undefined>(undefined);

  // Reset fork target cache whenever parent-supplied versionId changes
  useEffect(() => {
    forkTargetIdRef.current = undefined;
  }, [versionId]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!versionId || !debouncedOverrides) return;

    const saveChanges = async () => {
      const currentSaveId = ++saveCounterRef.current;
      setIsSaving(true);
      setError(null);

      try {
        let targetVersionId: UUID = forkTargetIdRef.current ?? versionId;

        // Fork once per session if visitorId present and we haven't forked yet
        if (visitorId && !forkTargetIdRef.current) {
          const forkResponse = await agentVersionsService.forkAgentVersion(versionId, { visitor_id: visitorId });

          if (!isApiError(forkResponse) && forkResponse?.id) {
            targetVersionId = forkResponse.id as UUID;
            forkTargetIdRef.current = targetVersionId;
            onForked?.(forkResponse);
            analytics.track('version_forked_client', { version_id: versionId, new_version_id: targetVersionId });
          }
        }

        const payload: any = { overrides: debouncedOverrides };
        if (visitorId) payload.visitor_id = visitorId;

        const response = await agentVersionsService.updateAgentVersion(targetVersionId, payload);

        if (currentSaveId === saveCounterRef.current) {
          if (isApiError(response)) {
            setError(response.message || 'Failed to save changes.');
            analytics.track('version_auto_save_error', {
              version_id: targetVersionId,
              error_code: response.status,
              http_status: response.status,
            });
          } else {
            setLastSaved(new Date());
            analytics.track('version_auto_save_success', { version_id: targetVersionId });
          }
          setIsSaving(false);
        }
      } catch {
        if (currentSaveId === saveCounterRef.current) {
          setError('A network error occurred while saving.');
          setIsSaving(false);
        }
      }
    };

    saveChanges();
  }, [debouncedOverrides, versionId, visitorId, onForked, analytics]);

  return { isSaving, error, lastSaved };
};
